import { useMemo, useRef, useState, useCallback } from "react";

import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  PlusOutlined,
  UndoOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import {
  Tooltip as AntDToolTip,
  Button,
  Collapse,
  Dropdown,
  Row,
  Space,
  Table,
  Popconfirm,
  message,
} from "antd";
import styled from "styled-components";

import { capitalizeFirstLetter, getCustomFieldsItems } from "common/utils";
import { collectorFullName } from "features/home/utils";
import UserModal from "features/userManagementTable/components/userModal";
import {
  activeUsersSelector,
  inactiveUsersSelector,
  useFetchRolesQuery,
  useFetchUsersPaginatedQuery,
  useFetchUsersQuery,
  useUpdateUserActivationMutation,
} from "features/userManagementTable/userManagementAPI";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import ChangePasswordModal from "features/userManagementTable/components/changePasswordModal";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { getColumnSearchProps } from "common/antd-utils";
import { useFetchAllUserUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/userUserDefinedFieldsAPI";
import { UserDefinedFieldValues } from "components/userDefinedFieldValues";
import { CursorTable } from "components/cursorTable";

const StyledRow = styled(Row)`
  margin-bottom: 8px;
`;

const StyledCollapse = styled(Collapse)`
  background-color: #ffffff;
`;

const StyledPanel = styled(Collapse.Panel)`
  .ant-collapse-header {
    align-items: center !important;
    padding: 0 !important;
  }

  .ant-collapse-content-box {
    padding: 8px 0 0 0 !important;
  }
`;

const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  font-size: 17px;
`;

export default function DeprecatedUsersTable() {
  const { data: roles } = useFetchRolesQuery();
  const {
    activeUsers: users,
    inactiveUsers,
    isLoading,
  } = useFetchUsersQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      activeUsers: activeUsersSelector(result),
      inactiveUsers: inactiveUsersSelector(result),
    }),
  });
  const { data: creditors } = useFetchCreditorSummariesQuery();
  const { data: userCustomFields = [] } = useFetchAllUserUdfCustomFieldsQuery();
  const [activeUserFilteredInfo, setActiveUserFilteredInfo] = useState({});
  const [inactiveUserFilteredInfo, setInactiveFilteredInfo] = useState({});
  const [updateUserActivation] = useUpdateUserActivationMutation();
  const {
    data: { [PERMISSIONS.USER__MANAGE]: isUserManagementAuthorized },
  } = useAuthorizations();

  const [isUserModalVisible, setUserModalVisible] = useState(false);
  const [isPasswordModalVisible, setPasswordModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);

  const activeUserSearchInput = useRef(null);
  const inactiveUserSearchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleAdd = () => {
    setActiveRecord(null);
    setUserModalVisible(true);
  };

  const handleEdit = (record) => {
    setActiveRecord(record);
    setUserModalVisible(true);
  };

  const handleDeactivateUser = async ({ userId }) => {
    const result = await updateUserActivation({
      userId,
      isActive: false,
    });

    if ("data" in result) {
      message.success("User deactivated successfully");
    }
  };

  const handleActivateUser = async ({ userId }) => {
    const result = await updateUserActivation({
      userId,
      isActive: true,
    });

    if ("data" in result) {
      message.success("User reactivated successfully");
    }
  };

  const handleChange = (_, filters) => {
    setActiveUserFilteredInfo(filters);
  };

  const handleInactiveChange = (_, filters) => {
    setInactiveFilteredInfo(filters);
  };

  const handleMenuClick = useCallback(({ item, domEvent }) => {
    item.props.actionHandler();
  }, []);

  const actionItems = (record) => [
    {
      label: "Change Password",
      key: "password",
      actionHandler: () => {
        setActiveRecord(record);
        setPasswordModalVisible(true);
      },
    },
  ];

  const typeOptions = useMemo(() => {
    if (users) {
      return [...new Set(users.map((user) => user.type))].map((type) => ({
        text: capitalizeFirstLetter(type),
        value: type,
      }));
    }
  }, [users]);

  const buildColumns = ({ filters, searchRef, actionsColumn }) => [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (_, record) => collectorFullName(record),
      ...getColumnSearchProps("name", searchRef, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filters?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Account Type",
      dataIndex: "type",
      key: "type",
      render: (type) => capitalizeFirstLetter(type),
      filters: typeOptions,
      // @ts-ignore
      filteredValue: filters?.type ?? null,
      onFilter: (value, record) => record?.type === value,
    },
    {
      title: "Role",
      dataIndex: "groupIds",
      key: "groupIds",
      render: (groupIds) => {
        const roleNames = roles?.filter((x) => groupIds?.includes(x.id)).map((x) => x.name) ?? [];
        return roleNames.length > 0 ? roleNames.join(", ") : "-";
      },
      filters:
        roles?.map((role) => ({
          text: role.name,
          value: role.id,
        })) ?? [],
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) => record.groupIds?.includes(value),
      // @ts-ignore
      filteredValue: filters?.groupIds ?? null,
    },
    {
      title: "Client",
      render: (text, record) => {
        const selectedCreditor = creditors?.find((creditor) => creditor.id === record.creditorId);
        return selectedCreditor ? (
          <>
            <div>Name: {selectedCreditor.name}</div>
            <div>Code: {selectedCreditor.code}</div>
          </>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "-",
      // @ts-ignore
      filteredValue: filters?.email ?? null,
      ...getColumnSearchProps("email", searchRef, handleSearch, handleReset),
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phoneNumber) => phoneNumber || "-",
    },
    {
      title: "Phone Extension",
      dataIndex: "phoneExtension",
      key: "phoneExtension",
      render: (phoneExtension) => phoneExtension || "-",
    },
    {
      title: "User Defined Fields",
      key: "customFields",
      dataIndex: "customFields",
      render: (_, record) => {
        const customFieldItems = getCustomFieldsItems(record.customFields, userCustomFields);

        // @ts-ignore
        return <UserDefinedFieldValues customFieldItems={customFieldItems} />;
      },
      visible: true,
    },
    actionsColumn,
  ];

  const columns = buildColumns({
    filters: activeUserFilteredInfo,
    searchRef: activeUserSearchInput,
    actionsColumn: {
      title: "Actions",
      fixed: "right",
      dataIndex: "actions",
      key: "actions",
      hidden: !isUserManagementAuthorized,
      render: (_, record) => (
        <Space size="middle">
          <AntDToolTip placement="bottom" title="Edit" key="edit">
            <EditOutlined key="edit" onClick={() => handleEdit(record)} />
          </AntDToolTip>
          <Popconfirm
            placement="topLeft"
            okText="Yes"
            title="Are you sure you want to deactivate this user?"
            onConfirm={() => handleDeactivateUser({ userId: record.id })}
          >
            <AntDToolTip placement="bottom" title="Deactivate" key="deactivate">
              <DeleteOutlined key="deactivate" />
            </AntDToolTip>
          </Popconfirm>
          <Dropdown
            menu={{
              items: actionItems(record),
              onClick: handleMenuClick,
            }}
            trigger={["click"]}
          >
            <Button shape="circle" type="text">
              <StyledEllipsisOutlined />
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  });

  const inactiveColumns = buildColumns({
    filters: inactiveUserFilteredInfo,
    searchRef: inactiveUserSearchInput,
    actionsColumn: {
      title: "Actions",
      fixed: "right",
      dataIndex: "actions",
      key: "actions",
      hidden: !isUserManagementAuthorized,
      render: (_, record) => (
        <Space size="middle">
          <Popconfirm
            placement="topLeft"
            okText="Yes"
            title="Are you sure you want to reactivate this user?"
            onConfirm={() => handleActivateUser({ userId: record.id })}
          >
            <AntDToolTip placement="bottom" title="Reactivate" key="reactivate">
              <UndoOutlined key="reactivate" />
            </AntDToolTip>
          </Popconfirm>
        </Space>
      ),
    },
  });

  return (
    <>
      <StyledRow align="middle">
        <span>Manage users and their roles.</span>
        {isUserManagementAuthorized && (
          <Button onClick={() => handleAdd()} icon={<PlusOutlined />} type="link">
            Add User
          </Button>
        )}
      </StyledRow>
      <StyledCollapse defaultActiveKey="activeUsers" bordered={false}>
        <StyledPanel header={<h3>Active Users</h3>} key="activeUsers">
          <Table
            loading={isLoading}
            bordered
            scroll={{ x: "max-content" }}
            // @ts-ignore
            columns={columns}
            dataSource={users}
            onChange={handleChange}
          />
        </StyledPanel>
      </StyledCollapse>
      <StyledCollapse defaultActiveKey="inactiveUsers" bordered={false}>
        <StyledPanel header={<h3>Inactive Users</h3>} key="inactiveUsers">
          <Table
            loading={isLoading}
            bordered
            scroll={{ x: "max-content" }}
            // @ts-ignore
            columns={inactiveColumns}
            dataSource={inactiveUsers}
            onChange={handleInactiveChange}
          />
        </StyledPanel>
      </StyledCollapse>
      {isUserModalVisible && (
        <UserModal
          title={activeRecord ? "Edit User" : "Add User"}
          selectedUser={activeRecord}
          open={isUserModalVisible}
          onOk={() => {
            setUserModalVisible(false);
            setActiveRecord(null);
          }}
          onCancel={() => {
            setUserModalVisible(false);
            setActiveRecord(null);
          }}
        />
      )}
      {isPasswordModalVisible && (
        <ChangePasswordModal
          title="Change Password"
          selectedUser={activeRecord}
          open={isPasswordModalVisible}
          onOk={() => {
            setPasswordModalVisible(false);
            setActiveRecord(null);
          }}
          onCancel={() => {
            setPasswordModalVisible(false);
            setActiveRecord(null);
          }}
        />
      )}
    </>
  );
}

const DEFAULT_PAGE_SIZE = 100;

export function ConsumerUsersTable() {
  const {
    data: { [PERMISSIONS.USER__MANAGE]: isUserManagementAuthorized },
  } = useAuthorizations();

  const [updateUserActivation] = useUpdateUserActivationMutation();

  // const { state } = useLocation();
  const [virtualPage, setVirtualPage] = useState(1);
  const [tableFilters, setTableFilters] = useState({});
  const [pagination, setPagination] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    prevToken: null,
    nextToken: null,
    orderBy: "last_name",
  });
  const {
    data: { results: users, meta: { prevToken, nextToken, pageSize, orderBy } } = {
      results: [],
      meta: { pageSize: DEFAULT_PAGE_SIZE, prevToken: null, nextToken: null },
    },
    isLoading: isFetchUsersLoading,
    isFetching: isFetchUsersFetching,
    refetch: fetchUsers,
  } = useFetchUsersPaginatedQuery(
    {
      ...tableFilters,
      isConsumer: true,
      virtualPage,
      ...pagination,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [activeRecord, setActiveRecord] = useState(null);
  const [isPasswordModalVisible, setPasswordModalVisible] = useState(false);

  const handleResetPassword = (record) => {
    setActiveRecord(record);
    setPasswordModalVisible(true);
  };

  const handleDeactivateUser = async ({ userId }) => {
    const result = await updateUserActivation({
      userId,
      isActive: false,
    });

    if ("data" in result) {
      message.success("User deactivated successfully");
      // Refetch users after deactivating
      fetchUsers();
    }
  };

  const handleTableChange = (newPagination, newFilters, sorter) => {
    if (newPagination && Object.keys(newPagination).length > 0) {
      setVirtualPage(newPagination.virtualPage);
      setPagination({
        pageSize: newPagination.pageSize,
        prevToken: newPagination.prevToken,
        nextToken: newPagination.nextToken,
        orderBy: pagination.orderBy,
      });
    } else {
      setPagination({
        pageSize,
        prevToken: null,
        nextToken: null,
        orderBy: pagination.orderBy,
      });
    }
  };

  const onPaginationChange = (newPage, newPageSize) => {
    setPagination({
      pageSize: newPageSize,
      prevToken: null,
      nextToken: null,
      orderBy: pagination.orderBy,
    });
    setVirtualPage(newPage);
  };

  const columns = [
    {
      title: "User ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Debtor",
      dataIndex: "name",
      key: "name",
      render: (_, record) => {
        return (
          <a href={`/debtors/${record.debtorId}`} target="_blank" rel="noreferrer">
            {record.name} ({record.debtorId})
          </a>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "actions",
      key: "actions",
      hidden: !isUserManagementAuthorized,
      render: (_, record) => (
        <Space size="middle">
          <AntDToolTip placement="bottom" title="Reset Password" key="reset-password">
            <UnlockOutlined key="reset-password" onClick={() => handleResetPassword(record)} />
          </AntDToolTip>
          <Popconfirm
            placement="topLeft"
            okText="Yes"
            title="Are you sure you want to deactivate this user?"
            onConfirm={() => handleDeactivateUser({ userId: record.id })}
          >
            <DeleteOutlined key="delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <StyledRow align="middle">
        <span>Manage consumer users.</span>
      </StyledRow>
      <CursorTable
        loading={isFetchUsersLoading || isFetchUsersFetching}
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={users}
        onChange={handleTableChange}
        pagination={{
          pagination: onPaginationChange,
        }}
        pageSize={pageSize}
        prevToken={prevToken}
        nextToken={nextToken}
        virtualPage={virtualPage}
        setVirtualPage={setVirtualPage}
      />
      {isPasswordModalVisible && (
        <ChangePasswordModal
          title="Change Password"
          selectedUser={activeRecord}
          open={isPasswordModalVisible}
          onOk={() => {
            setPasswordModalVisible(false);
            setActiveRecord(null);
          }}
          onCancel={() => {
            setPasswordModalVisible(false);
            setActiveRecord(null);
          }}
        />
      )}
    </>
  );
}
